import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { usePreventNavigation } from '../../hooks/usePreventNavigation';
import { apiClient } from '../../services/api';
import CopyButton from '../common/CopyButton';
import TransferProgressMessage from '../common/TransferProgressMessage';

interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface Props {
  credentials: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
    secretExpiryDate?: string;
    siteUrl: string;
    siteId: string;
    driveId: string;
  };
  onBack: () => void;
  redirectPath?: string | undefined;
}

const Step7Summary: React.FC<Props> = ({ credentials, onBack, redirectPath }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const { refreshUserDetails } = useAccount();
  const navigate = useNavigate();

  usePreventNavigation(isLoading);

  const handleSaveAndVerify = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // First, save the credentials
      await apiClient.post('/setSharepointCredentials', credentials);

      // Then, verify the connection and trigger migration (can take several minutes)
      const verificationResult = await apiClient.post(
        '/checkSharepointConnection',
        {},
        { timeout: 600000 } // 10 minute timeout for large library transfers
      );
      const data = verificationResult.data;

      if (data.success) {
        setIsVerified(true);
        await refreshUserDetails();
        setTimeout(
          () =>
            navigate(
              redirectPath
                ? `${redirectPath}&sharepoint_connected=true`
                : '/account?sharepoint_connected=true'
            ),
          2000
        );
      } else {
        setError(data.message || 'Verification failed. Please go back and check your credentials.');
      }
    } catch (err: unknown) {
      const error = err as ApiError;
      setError(error.response?.data?.message || error.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 7: Summary and Verification
      </Typography>
      <Typography variant="body2" color="text.primary" paragraph>
        Please review the credentials you have entered. The Client Secret is hidden for security.
      </Typography>

      <List sx={{ textAlign: 'left' }}>
        <ListItem secondaryAction={<CopyButton textToCopy={credentials.tenantId} />}>
          <ListItemText primary="✓ Tenant ID" secondary={credentials.tenantId} />
        </ListItem>
        <ListItem secondaryAction={<CopyButton textToCopy={credentials.clientId} />}>
          <ListItemText primary="✓ Client ID" secondary={credentials.clientId} />
        </ListItem>
        <ListItem secondaryAction={<CopyButton textToCopy={credentials.siteUrl} />}>
          <ListItemText primary="✓ Site URL" secondary={credentials.siteUrl} />
        </ListItem>
        <ListItem>
          <ListItemText primary="✓ Client Secret" secondary="********" />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="✓ Client Secret Expiry Date"
            secondary={credentials.secretExpiryDate || 'Not provided'}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="✓ API Permissions"
            secondary="Sites.Selected (Application) granted in Step 4"
          />
        </ListItem>
        <ListItem>
          <ListItemText primary="✓ Site Permissions" secondary="Write access granted in Step 5" />
        </ListItem>
        <ListItem secondaryAction={<CopyButton textToCopy={credentials.siteId} />}>
          <ListItemText primary="✓ Site ID" secondary={credentials.siteId} />
        </ListItem>
        <ListItem secondaryAction={<CopyButton textToCopy={credentials.driveId} />}>
          <ListItemText primary="✓ Drive ID" secondary={credentials.driveId} />
        </ListItem>
      </List>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {isVerified && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Connection successful! Redirecting...
        </Alert>
      )}

      {isLoading && (
        <TransferProgressMessage isLoading={isLoading} title="Verifying & Transferring Files" />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onBack} variant="outlined" disabled={isLoading || isVerified}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAndVerify}
            disabled={isLoading || isVerified}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Save & Verify Connection'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Step7Summary;
